import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Grow } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { useRestoreAccountMutation } from '@libs/graphql-types';
import Button from '@libs/ui/components/button';
import Typography from '@libs/ui/components/typography';

interface LeavingContentProps {
  name: string;
  deleteKey?: string;
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxWidth: 600,
    position: 'relative',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: theme.spacing(0, 6),
  },
}));

const LeavingContent = ({ name, deleteKey }: LeavingContentProps) => {
  const classes = useStyles();
  const { t } = useTranslation('public');
  const { enqueueSnackbar } = useSnackbar();
  const [restore, { loading }] = useRestoreAccountMutation({
    onCompleted(data) {
      const link =
        data?.restoreDeletedAccountByDeleteKey?.link_to_set_password_page;

      if (link) {
        window.location.href = link;
      } else {
        enqueueSnackbar(t('leave.somethingWentWrong'), { variant: 'error' });
        console.error(`Restore link: ${link}`);
      }
    },
  });
  const onSubmit = useCallback(() => {
    if (!deleteKey) {
      return;
    }
    restore({ variables: { delete_key: deleteKey } });
  }, [deleteKey, restore]);

  return (
    <Box className={classes.wrapper}>
      <Grow in timeout={500}>
        <div>
          <Typography
            fontWeight={500}
            variant="h4"
            paragraph
            fontStyle="italic"
          >
            {t('leave.heading')}
          </Typography>
          <Box>
            <Typography variant="body2" paragraph>
              {t('leave.body', { name })}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('leave.stillWant')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('leave.teamSignoff')}
            </Typography>
          </Box>
          {deleteKey && (
            <Box pt={2}>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                onClick={onSubmit}
                disabled={loading}
              >
                {t('leave.activateAccount')}
              </Button>
            </Box>
          )}
        </div>
      </Grow>
    </Box>
  );
};

export default LeavingContent;
